// /salish -- a public marine weather page for the corridor from Puget Sound
// to Desolation Sound.
//
// Everything the app already fetches for a trip map is here without an
// account: NWS point, land-zone and coastal-waters forecasts, ECCC marine
// forecasts and warnings, US and Canadian alerts, NDBC buoys, METARs,
// aviation advisories and NOAA/CHS tide predictions. The whole stack works
// unauthenticated -- the weather proxy is a Function URL with AuthType NONE,
// and every other source is called client-direct with open CORS -- so this
// route is a plain sibling of /support rather than living under /me.
//
// The page itself is a shell: a heading, the area buttons, and the lazily
// loaded map. It must stay maplibre-free, which is why nothing weather-shaped
// is imported here (useMapWeather transitively pulls maplibre-gl through
// WeatherPolygonsLayer). See SalishMap.jsx.
//
// Nothing on this page reads the database. That is not an accident: the
// gazetteer and every other Database.mjs table is userPool-authed and would
// come back silently empty for a signed-out visitor.

import { Suspense, useCallback, useMemo } from 'react';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { useSearchParams } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import { Spinner } from '../../components';
import { useDocumentTitle } from '../../utilities/useDocumentTitle';
import { SALISH_AREAS, areaBySlug } from './salishAreas.mjs';

// Lazy: SalishMap is the only import path that pulls in maplibre-gl
// (~250 KB gz); keep it out of the entry bundle.
const SalishMap = lazyWithReload(() => import('./SalishMap'), 'SalishMap');

const Salish = () => {
  useDocumentTitle('Salish Sea marine weather — TaskPogo');

  const [searchParams, setSearchParams] = useSearchParams();
  // Validated, never trusted raw. An unknown slug is the default view and the
  // URL is left saying whatever it said -- absent means "the default", the
  // way TripsCalendarSection treats a missing ?month=.
  const area = useMemo(() => areaBySlug(searchParams.get('area')), [searchParams]);

  // `replace`, always: Back should leave the page, not walk the user back
  // through every area they looked at.
  const selectArea = useCallback(
    (slug) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('area', slug);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return (
    <div className="py-4">
      <Container>
        <h1 className="h3 mb-2">Salish Sea marine weather</h1>
        <p className="text-body-secondary">
          Current conditions and forecasts from Puget Sound to Desolation Sound, drawn
          from the US National Weather Service, Environment and Climate Change Canada,
          NDBC buoys, airport observations, and NOAA and CHS tide predictions. Pick an
          area for a closer look.
        </p>
        <p className="small text-body-secondary">
          For planning only — not for navigation. Always consult official forecasts and
          notices to mariners before going out.
        </p>

        <div className="d-flex flex-wrap gap-2 mb-1" role="group" aria-label="Area">
          {SALISH_AREAS.map((option) => {
            const active = option.slug === area.slug;
            return (
              <Button
                key={option.slug}
                size="sm"
                variant={active ? 'primary' : 'outline-secondary'}
                aria-pressed={active}
                onClick={() => selectArea(option.slug)}
              >
                {option.label}
              </Button>
            );
          })}
        </div>
      </Container>

      <Suspense fallback={<Spinner />}>
        <SalishMap area={area} />
      </Suspense>
    </div>
  );
};

export default Salish;
